import type { UUID } from '@aline/common';
import { makeVar } from '@apollo/client';

/**
 * Canned fields whose typed value has not reached the server yet, keyed by
 * workflow. Finish reads the current workflow's set so a signer can't complete
 * on a stale value, and a write in another envelope can't block this one.
 *
 * Each write gets a revision because a field can be typed into again while an
 * earlier mutation is still in flight: only the newest revision may clear the
 * pending flag, so the older one settling leaves the field pending.
 */
export const pendingCannedFieldValueIdsByWorkflowIdVar = makeVar<Record<UUID, ReadonlySet<UUID>>>(
  {}
);

const EMPTY_PENDING_IDS: ReadonlySet<UUID> = new Set();

const writeRevisionByFieldKey = new Map<string, number>();
let nextWriteRevision = 0;

export function getPendingCannedFieldValueIds(
  workflowId: UUID | null | undefined
): ReadonlySet<UUID> {
  if (!workflowId) return EMPTY_PENDING_IDS;
  return pendingCannedFieldValueIdsByWorkflowIdVar()[workflowId] ?? EMPTY_PENDING_IDS;
}

export function beginCannedFieldValueWrite(workflowId: UUID, cannedFieldId: UUID): number {
  const fieldKey = getFieldKey(workflowId, cannedFieldId);
  nextWriteRevision += 1;
  const revision = nextWriteRevision;
  writeRevisionByFieldKey.set(fieldKey, revision);
  const pendingIds = new Set(getPendingCannedFieldValueIds(workflowId));
  pendingIds.add(cannedFieldId);
  writePendingIds(workflowId, pendingIds);
  return revision;
}

export function finishCannedFieldValueWrite(
  workflowId: UUID,
  cannedFieldId: UUID,
  revision: number
): void {
  if (!getIsCurrentCannedFieldValueWrite(workflowId, cannedFieldId, revision)) return;
  clearCannedFieldValueWrite(workflowId, cannedFieldId);
}

export function getIsCurrentCannedFieldValueWrite(
  workflowId: UUID,
  cannedFieldId: UUID,
  revision: number
): boolean {
  return writeRevisionByFieldKey.get(getFieldKey(workflowId, cannedFieldId)) === revision;
}

export function clearCannedFieldValueWrite(workflowId: UUID, cannedFieldId: UUID): void {
  writeRevisionByFieldKey.delete(getFieldKey(workflowId, cannedFieldId));
  const pendingIds = new Set(getPendingCannedFieldValueIds(workflowId));
  pendingIds.delete(cannedFieldId);
  writePendingIds(workflowId, pendingIds);
}

function getFieldKey(workflowId: UUID, cannedFieldId: UUID): string {
  return `${workflowId}:${cannedFieldId}`;
}

function writePendingIds(workflowId: UUID, ids: ReadonlySet<UUID>): void {
  const current = pendingCannedFieldValueIdsByWorkflowIdVar();
  if (ids.size === 0) {
    if (!(workflowId in current)) return;
    const next: Record<UUID, ReadonlySet<UUID>> = {};
    for (const id of Object.keys(current)) {
      const pendingIds = current[id];
      if (id !== workflowId && pendingIds) next[id] = pendingIds;
    }
    pendingCannedFieldValueIdsByWorkflowIdVar(next);
    return;
  }
  pendingCannedFieldValueIdsByWorkflowIdVar({ ...current, [workflowId]: ids });
}
